import {
  File,
  Briefcase,
  Banknote,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  XCircle,
  Star,
  Clock,
  User
} from 'lucide-react';

// Numeric to string type mapping
export const NOTIFICATION_TYPE_MAPPING = {
  0: 'New Proposal',
  1: 'New Quotation',
  2: 'New Negotiation'
};

// Notification type mappings
export const NOTIFICATION_TYPE_CONFIG = {
  'New Proposal': {
    icon: File,
    bgColor: 'bg-pri-light-1',
    iconColor: 'text-pri-dark-1',
    category: 'proposals'
  },
  'New Quotation': {
    icon: File,
    bgColor: 'bg-sec-light-1',
    iconColor: 'text-warning-dark-1',
    category: 'quotations'
  },
  'New Negotiation': {
    icon: MessageSquare,
    bgColor: 'bg-pri-light-1',
    iconColor: 'text-pri-dark-1',
    category: 'negotiations'
  },
  job_completed: {
    icon: CheckCircle,
    bgColor: 'bg-success-light-1',
    iconColor: 'text-success-dark-1',
    category: 'jobs'
  },
  system_message: {
    icon: MessageSquare,
    bgColor: 'bg-neu-light-2',
    iconColor: 'text-neu-dark-2',
    category: 'system'
  }
};

export const getNotificationIcon = (type) => {
  const baseClasses = 'flex items-center justify-center w-14 h-14 rounded-full';

  // Normalize the type first
  const normalizedType = normalizeNotificationType(type);

  // Handle legacy type names (for backward compatibility)
  if (normalizedType.includes('Job') || normalizedType.includes('job')) {
    return (
      <div className={`${baseClasses} bg-pri-light-1`}>
        <Briefcase size={24} className="text-pri-dark-1" />
      </div>
    );
  }

  if (normalizedType.includes('Quote') || normalizedType.includes('quote')) {
    return (
      <div className={`${baseClasses} bg-sec-light-1`}>
        <File size={24} className="text-warning-dark-1" />
      </div>
    );
  }

  if (normalizedType.includes('Payment') || normalizedType.includes('payment') || normalizedType.includes('Refund')) {
    return (
      <div className={`${baseClasses} bg-success-light-1`}>
        <Banknote size={24} className="text-success-dark-1" />
      </div>
    );
  }

  if (normalizedType.includes('Rate')) {
    return (
      <div className={`${baseClasses} bg-pri-light-1`}>
        <Star size={24} className="text-pri-dark-1" />
      </div>
    );
  }

  // Handle new type system
  const config = NOTIFICATION_TYPE_CONFIG[normalizedType];
  if (config) {
    const IconComponent = config.icon;
    return (
      <div className={`${baseClasses} ${config.bgColor}`}>
        <IconComponent size={24} className={config.iconColor} />
      </div>
    );
  }

  return (
    <div className={`${baseClasses} bg-neu-light-2`}>
      <MessageSquare size={24} className="text-neu-dark-2" />
    </div>
  );
};

// Convert numeric type to string type
export const normalizeNotificationType = (type) => {
  // If it's a number, map it to string
  if (typeof type === 'number' && NOTIFICATION_TYPE_MAPPING[type]) {
    return NOTIFICATION_TYPE_MAPPING[type];
  }

  // If it's already a string, return as is
  if (typeof type === 'string') {
    return type;
  }

  // Default fallback
  return 'system_message';
};

// Get notification category from type
export const getNotificationCategory = (type) => {
  const normalizedType = normalizeNotificationType(type);
  const config = NOTIFICATION_TYPE_CONFIG[normalizedType];
  return config?.category || 'system';
};

// Format notification time
export const formatNotificationTime = (timestamp) => {
  if (!timestamp) return 'Just now';

  const now = new Date();
  const notificationTime = new Date(timestamp);
  const diffInSeconds = Math.floor((now - notificationTime) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} hr${hours > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  } else {
    return notificationTime.toLocaleDateString();
  }
};

// Get action text based on notification type
export const getActionText = (type) => {
  const normalizedType = normalizeNotificationType(type);
  const actionTexts = {
    'New Proposal': 'View Proposal',
    'New Quotation': 'Review Quote',
    'New Negotiation': 'View Negotiation',
    job_completed: 'View Details',
    system_message: 'View Details'
  };

  return actionTexts[normalizedType] || 'View Details';
};

// Get notification title based on type
export const getNotificationTitle = (type) => {
  const normalizedType = normalizeNotificationType(type);

  // If we have a predefined title, use it
  const predefinedTitles = {
    'New Proposal': 'New Proposal',
    'New Quotation': 'New Quotation',
    'New Negotiation': 'New Negotiation',
    job_completed: 'Job Completed',
    system_message: 'System Message'
  };

  // Return predefined title or use the normalized type itself as title
  return predefinedTitles[normalizedType] || normalizedType || 'Notification';
};

// Mark all notifications as read
export const markAllNotificationsAsRead = async () => {
  try {
    // Import notificationService dynamically to avoid circular dependencies
    const { default: notificationService } = await import('../services/notificationService.jsx');

    const result = await notificationService.markAllAsRead();

    return result;
  } catch (error) {
    throw error;
  }
};
